import type {
    AttendanceManagementCommandStatus,
    IAttendanceCommandHistoryQuery,
    IAttendanceManagementCapabilities,
    IAttendanceManagementCommandHistoryResponse,
    IAttendanceManagementCommandHistoryResponseDto,
    IAttendanceManagementConfiguration,
    IAttendanceManagementConfigurationDto,
    IAttendanceManagementConfigurationSaveRequest,
    IAttendanceManagementTimingPair,
    IAttendanceManagementTimingValues,
    IAttendanceManagementWritebackSummaryResponseDto,
    IAttendanceManagementWritebackSummaryRow,
    IAttendanceOperationalRangeQuery,
    IAttendanceRequestFieldError,
    IAttendanceTimingConfigurationDto,
    IAttendanceTimingDefaultConfigurationDto,
} from "../models/AttendanceManagement";

const timePattern = /^([01]\d|2[0-3]):[0-5]\d$/;
const utcTimestampPattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/;
const maximumRangeMilliseconds = 31 * 24 * 60 * 60 * 1000;
const supportedStatusFilters = new Set([
    "Queued",
    "Processing",
    "Completed",
    "Failed",
    "DeadLettered",
    "Success",
]);

function createValidationError(
    field: string,
    code: string,
    message: string,
): IAttendanceRequestFieldError {
    return { field, code, message };
}

function toMinutes(value: string): number {
    const [hours, minutes] = value.split(":").map(Number);
    return (hours * 60) + minutes;
}

function isValidUtcTimestamp(value: string): boolean {
    return utcTimestampPattern.test(value) && !Number.isNaN(Date.parse(value));
}

function isValidMinuteValue(value: number): boolean {
    return Number.isInteger(value) && value >= 0 && value <= 1440;
}

function toTimingValues(
    sessionStartTime: string | null | undefined,
    sessionEndTime: string | null | undefined,
    preSessionGraceMinutes: number | null | undefined,
    onTimeGraceMinutes: number | null | undefined,
    earlyLateBandMinutes: number | null | undefined,
    lateLateBandMinutes: number | null | undefined,
): IAttendanceManagementTimingValues | null {
    if (typeof sessionStartTime !== "string"
        || typeof sessionEndTime !== "string"
        || typeof preSessionGraceMinutes !== "number"
        || typeof onTimeGraceMinutes !== "number"
        || typeof earlyLateBandMinutes !== "number"
        || typeof lateLateBandMinutes !== "number") {
        return null;
    }

    return {
        sessionStartTime,
        sessionEndTime,
        preSessionGraceMinutes,
        onTimeGraceMinutes,
        earlyLateBandMinutes,
        lateLateBandMinutes,
    };
}

function toDefaultTimingPair(
    configuration: IAttendanceTimingDefaultConfigurationDto,
): IAttendanceManagementTimingPair {
    return {
        am: {
            sessionStartTime: configuration.amSessionStartTime,
            sessionEndTime: configuration.amSessionEndTime,
            preSessionGraceMinutes: configuration.preSessionGraceMinutes,
            onTimeGraceMinutes: configuration.onTimeGraceMinutes,
            earlyLateBandMinutes: configuration.earlyLateBandMinutes,
            lateLateBandMinutes: configuration.lateLateBandMinutes,
        },
        pm: {
            sessionStartTime: configuration.pmSessionStartTime,
            sessionEndTime: configuration.pmSessionEndTime,
            preSessionGraceMinutes: configuration.pmPreSessionGraceMinutes,
            onTimeGraceMinutes: configuration.pmOnTimeGraceMinutes,
            earlyLateBandMinutes: configuration.pmEarlyLateBandMinutes,
            lateLateBandMinutes: configuration.pmLateLateBandMinutes,
        },
    };
}

function toDefaultTimingConfiguration(
    timing: IAttendanceManagementTimingPair,
): IAttendanceTimingDefaultConfigurationDto {
    return {
        amSessionStartTime: timing.am.sessionStartTime,
        amSessionEndTime: timing.am.sessionEndTime,
        preSessionGraceMinutes: timing.am.preSessionGraceMinutes,
        onTimeGraceMinutes: timing.am.onTimeGraceMinutes,
        earlyLateBandMinutes: timing.am.earlyLateBandMinutes,
        lateLateBandMinutes: timing.am.lateLateBandMinutes,
        pmSessionStartTime: timing.pm.sessionStartTime,
        pmSessionEndTime: timing.pm.sessionEndTime,
        pmPreSessionGraceMinutes: timing.pm.preSessionGraceMinutes,
        pmOnTimeGraceMinutes: timing.pm.onTimeGraceMinutes,
        pmEarlyLateBandMinutes: timing.pm.earlyLateBandMinutes,
        pmLateLateBandMinutes: timing.pm.lateLateBandMinutes,
    };
}

function toTimingConfigurationDto(
    configuration: IAttendanceManagementConfiguration["timingConfigurations"][number],
): IAttendanceTimingConfigurationDto {
    const timing: IAttendanceTimingConfigurationDto = {
        year: configuration.year,
        amUsesEstablishmentDefault: configuration.am.usesEstablishmentDefault,
        pmUsesEstablishmentDefault: configuration.pm.usesEstablishmentDefault,
    };

    if (!configuration.am.usesEstablishmentDefault && configuration.am.values) {
        timing.amSessionStartTime = configuration.am.values.sessionStartTime;
        timing.amSessionEndTime = configuration.am.values.sessionEndTime;
        timing.preSessionGraceMinutes = configuration.am.values.preSessionGraceMinutes;
        timing.onTimeGraceMinutes = configuration.am.values.onTimeGraceMinutes;
        timing.earlyLateBandMinutes = configuration.am.values.earlyLateBandMinutes;
        timing.lateLateBandMinutes = configuration.am.values.lateLateBandMinutes;
    }

    if (!configuration.pm.usesEstablishmentDefault && configuration.pm.values) {
        timing.pmSessionStartTime = configuration.pm.values.sessionStartTime;
        timing.pmSessionEndTime = configuration.pm.values.sessionEndTime;
        timing.pmPreSessionGraceMinutes = configuration.pm.values.preSessionGraceMinutes;
        timing.pmOnTimeGraceMinutes = configuration.pm.values.onTimeGraceMinutes;
        timing.pmEarlyLateBandMinutes = configuration.pm.values.earlyLateBandMinutes;
        timing.pmLateLateBandMinutes = configuration.pm.values.lateLateBandMinutes;
    }

    return restoreExplicitPmBands(timing);
}

function normaliseMark(value: string): string {
    return value.trim().toUpperCase();
}

function validateTimingValues(
    field: string,
    values: IAttendanceManagementTimingValues,
): IAttendanceRequestFieldError[] {
    const errors: IAttendanceRequestFieldError[] = [];

    if (!timePattern.test(values.sessionStartTime)) {
        errors.push(createValidationError(
            field,
            "INVALID_TIME",
            "Session start time must use HH:mm format.",
        ));
    }

    if (!timePattern.test(values.sessionEndTime)) {
        errors.push(createValidationError(
            field,
            "INVALID_TIME",
            "Session end time must use HH:mm format.",
        ));
    }

    if (timePattern.test(values.sessionStartTime)
        && timePattern.test(values.sessionEndTime)
        && toMinutes(values.sessionEndTime) <= toMinutes(values.sessionStartTime)) {
        errors.push(createValidationError(
            field,
            "INVALID_TIME_RANGE",
            "Session end time must be after session start time.",
        ));
    }

    [
        values.preSessionGraceMinutes,
        values.onTimeGraceMinutes,
        values.earlyLateBandMinutes,
        values.lateLateBandMinutes,
    ].forEach((value) => {
        if (!isValidMinuteValue(value)) {
            errors.push(createValidationError(
                field,
                "INVALID_MINUTES",
                "Timing minute values must be whole numbers from 0 through 1440.",
            ));
        }
    });

    return errors;
}

function validateYearTimingSession(
    field: string,
    session: IAttendanceManagementConfiguration["timingConfigurations"][number]["am"],
): IAttendanceRequestFieldError[] {
    if (session.usesEstablishmentDefault) {
        return session.values === null
            ? []
            : [createValidationError(
                field,
                "INHERITED_VALUES_NOT_ALLOWED",
                "An inheriting session cannot contain explicit timing values.",
            )];
    }

    if (session.values === null) {
        return [createValidationError(
            field,
            "REQUIRED",
            "An explicit session requires complete timing values.",
        )];
    }

    return validateTimingValues(field, session.values);
}

export function restoreExplicitPmBands(
    configuration: IAttendanceTimingConfigurationDto,
): IAttendanceTimingConfigurationDto {
    if (configuration.pmUsesEstablishmentDefault) {
        return { ...configuration };
    }

    return {
        ...configuration,
        pmPreSessionGraceMinutes: configuration.pmPreSessionGraceMinutes ?? configuration.preSessionGraceMinutes,
        pmOnTimeGraceMinutes: configuration.pmOnTimeGraceMinutes ?? configuration.onTimeGraceMinutes,
        pmEarlyLateBandMinutes: configuration.pmEarlyLateBandMinutes ?? configuration.earlyLateBandMinutes,
        pmLateLateBandMinutes: configuration.pmLateLateBandMinutes ?? configuration.lateLateBandMinutes,
    };
}

export function toAttendanceManagementViewModel(
    configuration: IAttendanceManagementConfigurationDto,
): IAttendanceManagementConfiguration {
    const defaultTimingConfiguration = configuration.defaultTimingConfiguration
        ? toDefaultTimingPair(configuration.defaultTimingConfiguration)
        : null;
    const capabilities: IAttendanceManagementCapabilities = {
        defaultTimingPresent: defaultTimingConfiguration !== null,
        explicitTimingEditsAllowed: true,
        inheritanceChangesAllowed: defaultTimingConfiguration !== null,
        isApiBacked: true,
    };

    return {
        timeZoneId: configuration.timeZoneId,
        writebackEnabled: typeof configuration.writebackEnabled === "boolean"
            ? configuration.writebackEnabled
            : null,
        liveDate: configuration.liveDate ?? null,
        availableYears: [...configuration.availableYears],
        defaultTimingConfiguration,
        timingConfigurations: configuration.timingConfigurations.map((timing) => {
            const normalizedTiming = restoreExplicitPmBands(timing);

            return {
                year: normalizedTiming.year,
                am: {
                    usesEstablishmentDefault: normalizedTiming.amUsesEstablishmentDefault,
                    values: normalizedTiming.amUsesEstablishmentDefault
                        ? null
                        : toTimingValues(
                            normalizedTiming.amSessionStartTime,
                            normalizedTiming.amSessionEndTime,
                            normalizedTiming.preSessionGraceMinutes,
                            normalizedTiming.onTimeGraceMinutes,
                            normalizedTiming.earlyLateBandMinutes,
                            normalizedTiming.lateLateBandMinutes,
                        ),
                },
                pm: {
                    usesEstablishmentDefault: normalizedTiming.pmUsesEstablishmentDefault,
                    values: normalizedTiming.pmUsesEstablishmentDefault
                        ? null
                        : toTimingValues(
                            normalizedTiming.pmSessionStartTime,
                            normalizedTiming.pmSessionEndTime,
                            normalizedTiming.pmPreSessionGraceMinutes,
                            normalizedTiming.pmOnTimeGraceMinutes,
                            normalizedTiming.pmEarlyLateBandMinutes,
                            normalizedTiming.pmLateLateBandMinutes,
                        ),
                },
            };
        }),
        codeCatalogue: configuration.codeCatalogue.map((entry) => ({ ...entry })),
        protectedMarks: [...configuration.protectedMarks],
        overwritableMarks: [...configuration.overwritableMarks],
        capabilities,
    };
}

export function toAttendanceManagementSaveRequest(
    configuration: IAttendanceManagementConfiguration,
): IAttendanceManagementConfigurationSaveRequest {
    return {
        defaultTimingConfiguration: configuration.defaultTimingConfiguration
            ? toDefaultTimingConfiguration(configuration.defaultTimingConfiguration)
            : null,
        timingConfigurations: configuration.timingConfigurations.map(toTimingConfigurationDto),
        enabledCodes: configuration.codeCatalogue
            .filter((entry) => entry.enabled)
            .map((entry) => entry.code),
        protectedMarks: [...configuration.protectedMarks],
        overwritableMarks: [...configuration.overwritableMarks],
    };
}

export function toWritebackSummaryRows(
    response: IAttendanceManagementWritebackSummaryResponseDto,
): IAttendanceManagementWritebackSummaryRow[] {
    return response.rows.map((row) => ({ ...row }));
}

export function toAttendanceManagementCommandStatus(status: number): AttendanceManagementCommandStatus {
    switch (status) {
    case 0:
        return "Queued";
    case 1:
        return "Processing";
    case 2:
        return "Completed";
    case 3:
        return "Failed";
    case 4:
        return "DeadLettered";
    default:
        return "Unknown";
    }
}

export function toCommandHistoryResponse(
    response: IAttendanceManagementCommandHistoryResponseDto,
): IAttendanceManagementCommandHistoryResponse {
    const commandRows = response.commands ?? response.rows ?? [];

    return {
        rows: commandRows.map((row) => ({
            commandId: row.commandId,
            studentName: row.studentName ?? null,
            studentMisId: row.studentMisId ?? null,
            year: row.year ?? null,
            class: row.class ?? null,
            commandType: row.commandType,
            status: toAttendanceManagementCommandStatus(row.status),
            numericStatus: row.status,
            resultCode: row.resultCode,
            resultMessage: row.resultMessage ?? null,
            attendanceDate: row.attendanceDate ?? null,
            sessionType: row.sessionType ?? null,
            attendanceMark: row.attendanceMark ?? null,
            subCode: row.subCode ?? null,
            target: row.target ?? null,
            sourceProduct: row.sourceProduct ?? null,
            submittedBy: row.submittedBy ?? null,
            createdUtc: row.createdUtc,
            processedUtc: row.processedUtc,
        })),
        page: response.page,
        pageSize: response.pageSize,
        totalCount: response.totalCount,
    };
}

export function validateAttendanceManagementConfiguration(
    configuration: IAttendanceManagementConfiguration,
): IAttendanceRequestFieldError[] {
    const errors: IAttendanceRequestFieldError[] = [];
    const availableYears = new Set(configuration.availableYears);
    const configuredYears = new Set<string>();

    if (configuration.defaultTimingConfiguration) {
        errors.push(...validateTimingValues(
            "DefaultTimingConfiguration.Am",
            configuration.defaultTimingConfiguration.am,
        ));
        errors.push(...validateTimingValues(
            "DefaultTimingConfiguration.Pm",
            configuration.defaultTimingConfiguration.pm,
        ));
    }

    configuration.timingConfigurations.forEach((timing, index) => {
        const field = `TimingConfigurations[${index}]`;

        if (!availableYears.has(timing.year)) {
            errors.push(createValidationError(
                `${field}.Year`,
                "INVALID_YEAR",
                "A timing configuration year must come from the available years.",
            ));
        }

        if (configuredYears.has(timing.year)) {
            errors.push(createValidationError(
                `${field}.Year`,
                "DUPLICATE_YEAR",
                "A timing configuration year can appear only once.",
            ));
        }
        configuredYears.add(timing.year);

        if (!configuration.defaultTimingConfiguration
            && (timing.am.usesEstablishmentDefault || timing.pm.usesEstablishmentDefault)) {
            errors.push(createValidationError(
                "DefaultTimingConfiguration",
                "REQUIRED",
                "A default timing configuration is required when a session inherits it.",
            ));
        }

        errors.push(...validateYearTimingSession(`${field}.Am`, timing.am));
        errors.push(...validateYearTimingSession(`${field}.Pm`, timing.pm));
    });

    const protectedMarks = new Set(configuration.protectedMarks.map(normaliseMark));
    const overlappingMarks = configuration.overwritableMarks
        .map(normaliseMark)
        .filter((mark) => mark.length > 0 && protectedMarks.has(mark));

    if (overlappingMarks.length > 0) {
        errors.push(createValidationError(
            "ProtectedMarks",
            "MARK_CONFLICT",
            "A mark cannot be both protected and overwritable.",
        ));
        errors.push(createValidationError(
            "OverwritableMarks",
            "MARK_CONFLICT",
            "A mark cannot be both protected and overwritable.",
        ));
    }

    return errors;
}

export function validateOperationalRange(
    query: IAttendanceOperationalRangeQuery,
): IAttendanceRequestFieldError[] {
    const errors: IAttendanceRequestFieldError[] = [];
    const hasValidFromUtc = isValidUtcTimestamp(query.fromUtc);
    const hasValidToUtc = isValidUtcTimestamp(query.toUtc);

    if (!hasValidFromUtc) {
        errors.push(createValidationError(
            "FromUtc",
            "INVALID_TIMESTAMP",
            "From UTC must be an ISO 8601 UTC timestamp.",
        ));
    }

    if (!hasValidToUtc) {
        errors.push(createValidationError(
            "ToUtc",
            "INVALID_TIMESTAMP",
            "To UTC must be an ISO 8601 UTC timestamp.",
        ));
    }

    if (!hasValidFromUtc || !hasValidToUtc) {
        return errors;
    }

    const rangeMilliseconds = Date.parse(query.toUtc) - Date.parse(query.fromUtc);

    if (rangeMilliseconds <= 0) {
        errors.push(createValidationError(
            "ToUtc",
            "INVALID_RANGE",
            "To UTC must be after From UTC.",
        ));
    } else if (rangeMilliseconds > maximumRangeMilliseconds) {
        errors.push(createValidationError(
            "ToUtc",
            "RANGE_TOO_LARGE",
            "The requested UTC range cannot exceed 31 days.",
        ));
    }

    return errors;
}

export function validateCommandHistoryQuery(
    query: IAttendanceCommandHistoryQuery,
): IAttendanceRequestFieldError[] {
    const errors = validateOperationalRange(query);

    if (!Number.isInteger(query.page) || query.page < 1 || query.page > 10000) {
        errors.push(createValidationError(
            "Page",
            "OUT_OF_RANGE",
            "Page must be a whole number from 1 through 10000.",
        ));
    }

    if (!Number.isInteger(query.pageSize) || query.pageSize < 1 || query.pageSize > 500) {
        errors.push(createValidationError(
            "PageSize",
            "OUT_OF_RANGE",
            "Page size must be a whole number from 1 through 500.",
        ));
    }

    if (query.class !== undefined && query.class.length > 50) {
        errors.push(createValidationError(
            "Class",
            "TOO_LONG",
            "Class must not exceed 50 characters.",
        ));
    }

    if (query.search !== undefined && query.search.length > 100) {
        errors.push(createValidationError(
            "Search",
            "TOO_LONG",
            "Search must not exceed 100 characters.",
        ));
    }

    if (query.status !== undefined && !supportedStatusFilters.has(query.status)) {
        errors.push(createValidationError(
            "Status",
            "INVALID_STATUS",
            "Status must be a documented command-history status.",
        ));
    }

    return errors;
}